import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

// Create context
const CMSContext = createContext(null);

// Provider component
export const CMSProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [settings, setSettings] = useState({});
  const [categories, setCategories] = useState([]);
  const [pressItems, setPressItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load all data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [productsRes, bannersRes, settingsRes, categoriesRes, pressRes] = await Promise.all([
          api('/products'),
          api('/banners'),
          api('/settings'),
          api('/categories'),
          api('/press-items'),
        ]);
        setProducts(productsRes.products || []);
        setBanners(bannersRes.banners || []);
        setSettings(settingsRes.settings || {});
        setCategories(categoriesRes.categories || []);
        setPressItems(pressRes.pressItems || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // ── Product actions ──

  const addProduct = useCallback(async (product) => {
    try {
      const res = await api('/admin/products', { method: 'POST', body: product });
      setProducts(prev => [...prev, res.product]);
      return res.product;
      } catch (err) {
        setError(err.message || 'Failed to create product');
        throw err;
        }
  }, []);

  const updateProduct = useCallback(async (product) => {
    try {
      const res = await api(`/admin/products/${product.id}`, { method: 'PUT', body: product });
      setProducts(prev => prev.map(p => p.id === product.id ? res.product : p));
      return res.product;
      } catch (err) {
      setError(err.message || 'Failed to update product');
      throw err;
      }
  }, []);

  const deleteProduct = useCallback(async (id) => {
    try {
      await api(`/admin/products/${id}`, { method: 'DELETE' });
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const getProduct = useCallback((idOrSlug) => {
    return products.find(p => p.id === idOrSlug || p.slug === idOrSlug);
  }, [products]);

  const getActiveProducts = useCallback(() => {
    return products.filter(p => p.active);
  }, [products]);

  const getFeaturedProducts = useCallback(() => {
    return products.filter(p => p.active && p.featured);
  }, [products]);

  // ── Banner actions ──

  const addBanner = useCallback(async (banner) => {
    try {
      const res = await api('/admin/banners', { method: 'POST', body: banner });
      setBanners(prev => [...prev, res.banner]);
      return { success: true };
    } catch (err) {
      return { success: false, errors: err.errors || [err.message] };
    }
  }, []);

  const updateBanner = useCallback(async (banner) => {
    try {
      const res = await api(`/admin/banners/${banner.id}`, { method: 'PUT', body: banner });
      setBanners(prev => prev.map(b => b.id === banner.id ? res.banner : b));
      return { success: true };
    } catch (err) {
      return { success: false, errors: err.errors || [err.message] };
    }
  }, []);

  const deleteBanner = useCallback(async (id) => {
    try {
      await api(`/admin/banners/${id}`, { method: 'DELETE' });
      setBanners(prev => prev.filter(b => b.id !== id));
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const getActiveBanners = useCallback((type = null) => {
    let filtered = banners.filter(b => b.active);
    if (type) {
      filtered = filtered.filter(b => b.type === type);
    }
    return filtered.sort((a, b) => a.order - b.order);
  }, [banners]);

  // ── Settings actions ──

  const updateSettings = useCallback(async (newSettings) => {
    try {
      const res = await api('/admin/settings', { method: 'PUT', body: newSettings });
      setSettings(prev => ({ ...prev, ...(res.settings || newSettings) }));
      return { success: true };
    } catch (err) {
      return { success: false, errors: err.errors || [err.message] };
    }
  }, []);

  // ── Category actions ──

  const loadAdminCategories = useCallback(async () => {
    const res = await api('/admin/categories');
    setCategories(res.categories || []);
  }, []);

  const addCategory = useCallback(async (category) => {
    try {
      const res = await api('/admin/categories', { method: 'POST', body: category });
      setCategories(prev => [...prev, res.category]);
      return { success: true, category: res.category };
    } catch (err) {
      return { success: false, errors: err.errors || [err.message] };
    }
  }, []);

  const updateCategory = useCallback(async (category) => {
    try {
      const res = await api(`/admin/categories/${category.id}`, { method: 'PUT', body: category });
      setCategories(prev => prev.map(c => c.id === category.id ? res.category : c));
      return { success: true, category: res.category };
    } catch (err) {
      return { success: false, errors: err.errors || [err.message] };
    }
  }, []);

  const deleteCategory = useCallback(async (id) => {
    try {
      await api(`/admin/categories/${id}`, { method: 'DELETE' });
      setCategories(prev => prev.filter(c => c.id !== id));
      return { success: true };
    } catch (err) {
      return { success: false, errors: err.errors || [err.message] };
    }
  }, []);

  // Returns all active categories sorted by navOrder (these are the top-level nav sections)
  const getNavCategories = useCallback(() => {
    return categories.filter(c => c.active).sort((a, b) => (a.navOrder ?? 0) - (b.navOrder ?? 0));
  }, [categories]);

  // Returns active products for a category key that should appear in the nav dropdown
  const getNavProducts = useCallback((categoryKey) => {
    return products
      .filter(p => p.active && p.category === categoryKey && p.showInNav !== false)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }, [products]);

  // Keep for backward compatibility
  const getCategoriesByNav = getNavCategories;

  // ── Press Item actions ──

  const loadAdminPressItems = useCallback(async () => {
    const res = await api('/admin/press-items');
    setPressItems(res.pressItems || []);
  }, []);

  const addPressItem = useCallback(async (item) => {
    try {
      const res = await api('/admin/press-items', { method: 'POST', body: item });
      setPressItems(prev => [...prev, res.pressItem]);
      return { success: true };
    } catch (err) {
      return { success: false, errors: err.errors || [err.message] };
    }
  }, []);

  const updatePressItem = useCallback(async (item) => {
    try {
      const res = await api(`/admin/press-items/${item.id}`, { method: 'PUT', body: item });
      setPressItems(prev => prev.map(p => p.id === item.id ? res.pressItem : p));
      return { success: true };
    } catch (err) {
      return { success: false, errors: err.errors || [err.message] };
    }
  }, []);

  const deletePressItem = useCallback(async (id) => {
    try {
      await api(`/admin/press-items/${id}`, { method: 'DELETE' });
      setPressItems(prev => prev.filter(p => p.id !== id));
      return { success: true };
    } catch (err) {
      return { success: false, errors: err.errors || [err.message] };
    }
  }, []);

  // ── Reset ──

  const resetToDefaults = useCallback(() => {
    // No-op with API backend — defaults are managed server-side
  }, []);

  const value = {
    // State
    products,
    banners,
    settings,
    categories,
    pressItems,
    loading,
    error,

    // Product actions
    addProduct,
    updateProduct,
    deleteProduct,
    getProduct,
    getActiveProducts,
    getFeaturedProducts,

    // Banner actions
    addBanner,
    updateBanner,
    deleteBanner,
    getActiveBanners,

    // Settings actions
    updateSettings,

    // Category actions
    loadAdminCategories,
    addCategory,
    updateCategory,
    deleteCategory,
    getNavCategories,
    getNavProducts,
    getCategoriesByNav,

    // Press Item actions
    loadAdminPressItems,
    addPressItem,
    updatePressItem,
    deletePressItem,

    // Reset
    resetToDefaults,
  };

  return (
    <CMSContext.Provider value={value}>
      {children}
    </CMSContext.Provider>
  );
};

// Custom hook to use CMS context
export const useCMS = () => {
  const context = useContext(CMSContext);
  if (!context) {
    throw new Error('useCMS must be used within a CMSProvider');
  }
  return context;
};

export default CMSContext;
